import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { type ToasterProps, Toaster, toast } from 'sonner';
import { CheckIcon, InfoIcon, WarningIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/components/admin/use-theme';
import { useOptionalSidebar } from '@/components/ui/sidebar';
import {
  CloseNotificationContext,
  useNotificationContext,
  useTakeUndoableMutation,
  useTranslate,
} from 'ra-core';

/**
 * Filled circular icon badge with a soft halo ring — the focal point of each
 * toast. Semantic color lives here (and only here); the toast surface stays
 * neutral. `ring` gives the glow halo seen in premium toasts.
 */
function ToastIcon({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        // 34px filled circle with a hairline white border (per spec) — no halo.
        'flex size-[34px] shrink-0 items-center justify-center rounded-full border border-white/[0.07] text-white',
        className
      )}
    >
      {children}
    </span>
  );
}

// Dev convenience: preview any toast variant from the browser console, e.g.
//   toast.error('Update failed');  toast.warning('Heads up');  toast.info('FYI')
// Stripped from production builds.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { toast: typeof toast }).toast = toast;
}

const toastIcons = {
  success: (
    <ToastIcon className="bg-emerald">
      <CheckIcon weight="bold" className="size-4" />
    </ToastIcon>
  ),
  // Alert (warning) glyph reads as "attention" / "something to address";
  // an X reads as "operation cancelled / dismissed" which isn't what an
  // error toast usually means.
  error: (
    <ToastIcon className="bg-destructive">
      <WarningIcon weight="fill" className="size-4" />
    </ToastIcon>
  ),
  warning: (
    <ToastIcon className="bg-amber">
      <WarningIcon weight="fill" className="size-4" />
    </ToastIcon>
  ),
  info: (
    <ToastIcon className="bg-cobalt">
      <InfoIcon weight="fill" className="size-4" />
    </ToastIcon>
  ),
};

/**
 * Displays notifications triggered with the useNotify hook.
 *
 * Supports different notification types (info, success, warning, error) and undoable mutations.
 * Automatically adapts to the current theme (light/dark).
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/notification Notification documentation}
 * @see {@link https://marmelab.com/ra-core/usenotify/ useNotify hook}
 *
 * @example
 * // Trigger a notification
 * import { useNotify } from 'ra-core';
 *
 * const NotifyButton = () => {
 *   const notify = useNotify();
 *   const handleClick = () => {
 *     notify('Comment approved', { type: 'success' });
 *   };
 *   return <button onClick={handleClick}>Notify</button>;
 * };
 */
export const Notification = (props: ToasterProps) => {
  const translate = useTranslate();
  const { notifications, takeNotification } = useNotificationContext();
  const takeMutation = useTakeUndoableMutation();
  const { theme } = useTheme();
  // Center the toast against the *main work area* (right of the sidebar), not
  // the full viewport — otherwise it drifts left under the sidebar. Rather than
  // hand-compute the offset from the sidebar width (brittle: it breaks when the
  // sidebar width, collapse state, the app's max-width centering, or the layout
  // shell-gap change), MEASURE the content column (`#layout-scroll`) and shift
  // by the difference between its centre and the viewport centre.
  //  • no SidebarProvider (auth screens) or mobile → no shift (viewport-centred)
  const sidebar = useOptionalSidebar();
  const hasSidebar = !!sidebar;
  const sidebarState = sidebar?.state;
  const sidebarIsMobile = sidebar?.isMobile;
  const [toasterXShift, setToasterXShift] = useState('0px');

  useEffect(() => {
    if (!hasSidebar || sidebarIsMobile) {
      setToasterXShift('0px');
      return;
    }
    const measure = () => {
      const el = document.getElementById('layout-scroll');
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const contentCenter = rect.left + rect.width / 2;
      setToasterXShift(
        `${Math.round(contentCenter - window.innerWidth / 2)}px`
      );
    };
    measure();
    // Re-measure after the sidebar collapse/expand width transition (~200ms)
    // settles, and on any viewport resize.
    const settleTimer = window.setTimeout(measure, 240);
    window.addEventListener('resize', measure);
    return () => {
      window.clearTimeout(settleTimer);
      window.removeEventListener('resize', measure);
    };
  }, [hasSidebar, sidebarState, sidebarIsMobile]);

  useEffect(() => {
    if (notifications.length) {
      const notification = takeNotification();
      if (notification) {
        const { message, type = 'info', notificationOptions } = notification;
        const { messageArgs, undoable } = notificationOptions || {};

        const beforeunload = (e: BeforeUnloadEvent) => {
          e.preventDefault();
          const confirmationMessage = '';
          e.returnValue = confirmationMessage;
          return confirmationMessage;
        };

        if (undoable) {
          window.addEventListener('beforeunload', beforeunload);
        }

        const mutation = takeMutation();

        const handleExited = () => {
          if (undoable) {
            if (mutation) {
              mutation({ isUndo: false });
            }
            window.removeEventListener('beforeunload', beforeunload);
          }
        };

        const handleUndo = () => {
          if (mutation) {
            mutation({ isUndo: true });
          }
          window.removeEventListener('beforeunload', beforeunload);
        };

        const finalMessage = message
          ? typeof message === 'string'
            ? translate(message, messageArgs)
            : React.isValidElement(message)
              ? message
              : undefined
          : undefined;

        toast[type](finalMessage, {
          action: undoable
            ? {
                label: translate('ra.action.undo'),
                onClick: handleUndo,
              }
            : undefined,
          onDismiss: handleExited,
          onAutoClose: handleExited,
        });
      }
    }
  }, [notifications, takeMutation, takeNotification, translate]);

  const handleRequestClose = useCallback(() => {
    // Dismiss all toasts
    toast.dismiss();
  }, []);

  return (
    <CloseNotificationContext.Provider value={handleRequestClose}>
      <Toaster
        theme={theme}
        closeButton
        // Bottom-center per design — easier to glance at without breaking
        // attention from the form. The bottom-center ScrollPill can briefly
        // overlap a toast; toasts auto-dismiss quickly enough that this is
        // a non-issue in practice.
        position="bottom-center"
        // Custom filled-badge icons (the green-circle vibe); neutral surface
        // so semantic color lives only in the badge.
        icons={toastIcons}
        gap={10}
        offset={24}
        style={
          {
            '--width': '720px',
            '--toaster-x-shift': toasterXShift,
          } as React.CSSProperties
        }
        className="toaster group"
        toastOptions={{
          classNames: {
            // Tinted pill: semantic light-fill background (set per type below),
            // no border, fully rounded. Content-sized (w-fit) so short toasts
            // don't stretch. Even 4px inset on left/top/bottom; 12px right for
            // the close button.
            // `min-h-[42px]` is essential: the badge is taller than the text,
            // so without it the pill collapses to the text height and the 34px
            // badge pokes out top & bottom. With it, the badge centers cleanly.
            // `!left-0 !right-0 !mx-auto`: sonner anchors each absolutely-
            // positioned toast at `left:0` inside the fixed `--width` (720px)
            // container, so a `w-fit` pill hugs the left edge instead of
            // centering under the work area. Pinning both insets to 0 and
            // letting auto margins resolve the slack centers the pill within
            // the (already content-centered) container without touching
            // sonner's `transform` (reserved for stacking/swipe).
            toast:
              'group toast !flex !items-center !min-h-[42px] !w-fit !max-w-[720px] !left-0 !right-0 !mx-auto !rounded-full !border-0 !backdrop-blur-md !text-foreground !shadow-elev-3 !py-1 !pl-2 !pr-10 !font-sans',
            // Let the title flow on a single line whenever it fits the pill,
            // and only wrap (`whitespace-normal`) when content actually
            // overflows the max-width — preserves natural readability.
            title:
              '!text-base !font-medium !text-foreground !leading-5 !whitespace-normal',
            // Per-type tint — light variant of the semantic color so the
            // pill reads as "info / success / warning / error" at a glance
            // without sacrificing text contrast.
            success: '!bg-emerald-light',
            error: '!bg-ruby-light',
            warning: '!bg-amber-light',
            info: '!bg-cobalt-light',
            description: '!text-xs !text-muted-foreground !leading-snug',
            // Size the icon wrapper to the 34px badge — sonner's default icon
            // box is ~16px, so the badge overflows it and the margin lands
            // under the badge (collapsing the gap). With a matching size, the
            // `mr-3` sits correctly after the badge.
            icon: '!size-[34px] !mr-3 !shrink-0 !self-center',
            // Off-white X, 12px from the right edge (per spec). `transform-none`
            // clears sonner's own translate(-35%,-35%) which would otherwise
            // stack with Tailwind v4's `translate`-based centering.
            closeButton:
              '!left-auto !right-3 !top-1/2 !-translate-y-1/2 !transform-none !size-5 !rounded-md !border-0 !bg-transparent !text-foreground/60 hover:!text-foreground hover:!bg-white/10 !opacity-100 !transition-colors',
            actionButton:
              '!bg-foreground !text-background !rounded-md !text-xs !font-medium',
            cancelButton:
              '!bg-muted !text-muted-foreground !rounded-md !text-xs',
          },
        }}
        {...props}
      />
    </CloseNotificationContext.Provider>
  );
};
